import React, { Component } from 'react';
import { Button, Form, Modal, Row, Col, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { GoCheck, GoX, GoWorkflow, GoSync, GoClock } from 'react-icons/go';
import Select from 'components/controls/select';
import Editor from "components/controls/editor";
import {
  SESSION_ALIAS_HTML5_PATTERN, SESSION_ALIAS_MIN_LENGTH, SESSION_ALIAS_MAX_LENGTH, validateSessionAlias
} from 'context/sessionAlias';
import { isIdShaped } from 'context/workflowReference';
import {
  WORKFLOW_IN_TARGET, WORKFLOW_FROM_BLOB, organizationHint, targetWorkflowHelp, checkTarget, checkWorkflow,
  checkInputData, readScheduleForm
} from './scheduleForm';
import { listTargetDefinitions } from './targetDefinitions';
import './create.css';

const DESCRIPTION_MAX_LENGTH = 256;

function getTimezoneOptions() {
  const options = [{ value: "UTC", label: "UTC" }];
  try {
    const timezones = Intl.supportedValuesOf('timeZone');
    for (const tz of timezones) {
      options.push({ value: tz, label: tz });
    }
  } catch (err) {
    // Fallback: UTC-only list
  }
  return options;
}

const TIMEZONE_OPTIONS = getTimezoneOptions();

export default class ScheduleModal extends Component {
  constructor(props) {
    super(props);
    this.form = React.createRef();
    this.state = this.getInitialState();
  }

  getInitialState() {
    return {
      enabled: true,
      repeat: "recurring_cron",
      cronExpression: "",
      invocationTime: "",
      timezone: "",
      startDate: "",
      endDate: "",
      targetType: "workflow",
      org: "",
      blob: "",
      revision: "default",
      session: "",
      workflowMode: WORKFLOW_IN_TARGET,
      workflowDefinition: "",
      sourceOrg: "",
      sourceBlob: "",
      sourceRevision: "default",
      sourceDefinition: "",
      description: "",
      inputData: JSON.stringify([], null, 2),
      alias: "",
      aliasError: null,
      targetError: null,
      workflowError: null,
      suggestionsKey: null,
      suggestions: []
    };
  }

  // Strip trailing Z and seconds/ms to get datetime-local input format.
  // Used for start/end dates that come back from the server as "YYYY-MM-DDTHH:MM:SSZ".
  utcStringToDatetimeLocal(s) {
    if ( !s ) return "";
    const trimmed = s.endsWith("Z") ? s.slice(0, -1) : s;
    return trimmed.slice(0, 16);
  }

  // Pass through datetime-local input value (no timezone conversion).
  // Used for invocation_time which is in the schedule's timezone, not UTC.
  naiveDatetimeStringToDatetimeLocal(s) {
    if ( !s ) return "";
    return s.slice(0, 16);
  }

  onEnter = () => {
    if ( this.props.schedule ) {
      const schedule = this.props.schedule;
      this.setState({
        ...this.getInitialState(),
        enabled: schedule.enabled !== false,
        repeat: schedule.repeat || "recurring_cron",
        cronExpression: schedule.cron_expression || "",
        invocationTime: this.naiveDatetimeStringToDatetimeLocal(schedule.invocation_time),
        timezone: schedule.timezone || "",
        startDate: this.utcStringToDatetimeLocal(schedule.start_date),
        endDate: this.utcStringToDatetimeLocal(schedule.end_date),
        targetType: schedule.invocation_target_type || "workflow",
        ...readScheduleForm(schedule),
        alias: schedule.alias || ""
      });
    } else {
      this.setState(this.getInitialState());
    }
  }

  // User enters start/end dates in UTC; append Z suffix. Input format from
  // datetime-local is "YYYY-MM-DDTHH:MM" (seconds defaulted to :00).
  datetimeLocalToUtcString(dateString) {
    if ( !dateString ) {
      return null;
    }
    const withSeconds = dateString.length === 16 ? `${dateString}:00` : dateString;
    return `${withSeconds}Z`;
  }

  // Invocation time is in the schedule's own timezone (not UTC). Send as a
  // naive ISO datetime with no trailing Z.
  datetimeLocalToNaiveString(dateString) {
    if ( !dateString ) {
      return null;
    }
    return dateString.length === 16 ? `${dateString}:00` : dateString;
  }

  handleSubmit = () => {
    if ( !this.form.current.reportValidity() ) {
      return;
    }

    // The alias is optional: a blank field is valid. The grammar check runs after reportValidity()
    // so the native pattern/length constraints report first and this message is ours.
    const alias = this.state.alias.trim();
    if ( alias ) {
      const aliasError = validateSessionAlias(alias);
      if ( aliasError ) {
        this.setState({ aliasError: aliasError });
        return;
      }
    }
    this.setState({ aliasError: null });

    const target = checkTarget({
      org: this.state.org, blob: this.state.blob, revision: this.state.revision, session: this.state.session
    });
    const workflow = checkWorkflow({
      mode: this.state.workflowMode,
      definition: this.state.workflowDefinition,
      source: {
        org: this.state.sourceOrg, blob: this.state.sourceBlob,
        revision: this.state.sourceRevision, definition: this.state.sourceDefinition
      }
    }, target.target.revision);
    const input = checkInputData(this.state.inputData);
    this.setState({ targetError: target.error, workflowError: workflow.error });
    if ( target.error || workflow.error || input.error ) {
      return;
    }

    const schedule = {
      alias: alias,
      description: this.state.description.trim(),
      enabled: this.state.enabled,
      repeat: this.state.repeat,
      timezone: this.state.timezone,
      startDate: this.datetimeLocalToUtcString(this.state.startDate),
      endDate: this.datetimeLocalToUtcString(this.state.endDate),
      invocationTarget: { target: target.target, workflow: workflow.workflow, input_data: input.inputData }
    };
    if ( "recurring_cron" === this.state.repeat ) {
      schedule.cronExpression = this.state.cronExpression;
    } else {
      schedule.invocationTime = this.datetimeLocalToNaiveString(this.state.invocationTime);
    }
    this.props.handleSubmit(schedule);
    this.props.onHide();
  }

  editTarget(patch) {
    const cleared = { targetError: null };
    // The workflow's check reads the target's revision, so changing it voids the workflow's refusal.
    if ( undefined !== patch.revision ) {
      cleared.workflowError = null;
    }
    this.setState({ ...patch, ...cleared });
  }

  editWorkflow(patch) {
    this.setState({ ...patch, workflowError: null });
  }

  isInvalid(errorKey, field) {
    return !!this.state[errorKey] && field === this.state[errorKey].field;
  }

  loadWorkflowSuggestions = () => {
    const org = this.state.org.trim();
    const blob = this.state.blob.trim();
    const revision = this.state.revision.trim();
    if ( !blob || !revision || ( !org && !isIdShaped(blob) ) ) {
      if ( this.state.suggestions.length || null !== this.state.suggestionsKey ) {
        this.setState({ suggestions: [], suggestionsKey: null });
      }
      return;
    }
    const key = [org, blob, revision].join("/");
    if ( key === this.state.suggestionsKey ) {
      return;
    }
    this.setState({ suggestionsKey: key, suggestions: [] });
    // An answer for an earlier target is dropped; a failure is asked again on the next focus.
    listTargetDefinitions(this.props.app.api, org || this.props.orgId, blob, revision).then(
      definitions => this.setState(state => key === state.suggestionsKey ? { suggestions: definitions } : null),
      () => this.setState(state => key === state.suggestionsKey ? { suggestionsKey: null } : null));
  }

  render() {
    const isEdit = !!this.props.schedule;
    const title = isEdit ? "Edit Schedule" : "Create a New Schedule";
    const submitLabel = isEdit ? "Save" : "Create";

    const enabledOptions = [
      { name: "Enabled", value: true, icon: <GoCheck/> },
      { name: "Disabled", value: false, icon: <GoX/> }
    ];

    const repeatOptions = [
      { name: "Recurring (Cron)", value: "recurring_cron", icon: <GoSync/> },
      { name: "One-time", value: "one_time", icon: <GoClock/> }
    ];

    const targetOptions = [
      { name: "Workflow", value: "workflow", icon: <GoWorkflow/> }
    ];

    const workflowModeOptions = [
      { name: "In The Target Revision", value: WORKFLOW_IN_TARGET },
      { name: "From Another Blob", value: WORKFLOW_FROM_BLOB }
    ];
    const inputError = checkInputData(this.state.inputData).error;

    return (
      <Modal
        show={this.props.show}
        onHide={this.props.onHide}
        onEnter={this.onEnter}
        onExited={this.onEnter}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form ref={this.form} onSubmit={(event) => {
            event.preventDefault();
            this.handleSubmit();
          }}>
            <Form.Group>
              <Form.Label>
                <strong>State</strong>
              </Form.Label>
              <Row>
                <Col sm={12}>
                  <ButtonGroup toggle size="sm" className="scheduler-toggle">
                    {enabledOptions.map((option, idx) => (
                      <ToggleButton
                        key={idx}
                        id={`enabled-${idx}`}
                        type="radio"
                        variant="light"
                        name="enabled"
                        value={String(option.value)}
                        checked={this.state.enabled === option.value}
                        onChange={() => this.setState({ enabled: option.value })}
                      >
                        <span style={{display: "inline-flex", alignItems: "center", gap: "6px"}}>
                          {option.icon} {option.name}
                        </span>
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                </Col>
              </Row>
            </Form.Group>

            <Form.Group>
              <Form.Label>
                <strong>Schedule Alias</strong>
              </Form.Label>
              <Row>
                <Col sm={12}>
                  <Form.Control
                    type="text" size="sm"
                    value={this.state.alias}
                    onChange={(e) => this.setState({ alias: e.target.value, aliasError: null })}
                    placeholder="e.g. nightly-build, daily_report"
                    pattern={SESSION_ALIAS_HTML5_PATTERN}
                    minLength={SESSION_ALIAS_MIN_LENGTH}
                    maxLength={SESSION_ALIAS_MAX_LENGTH}
                    title={"Lower-case letters, digits, underscores and hyphens only — " +
                      "for example primary, staging or daily_report."}
                  />
                </Col>
              </Row>
              <Form.Text muted>
                A stable name for <strong>this schedule</strong>, usable in place of its ID — not the workflow it
                {" "}runs. Schedule alias must conform to the following requirements:
                <ul>
                  <li>minimum of {SESSION_ALIAS_MIN_LENGTH} characters;</li>
                  <li>maximum of {SESSION_ALIAS_MAX_LENGTH} characters;</li>
                  <li>only lower-case alphanumeric characters along with <strong>-</strong>,<strong>_</strong> are
                    {" "}allowed;</li>
                  <li>must be unique within the scope of the revision.</li>
                </ul>
              </Form.Text>
              <Form.Control.Feedback type="invalid"
                style={{"display": this.state.aliasError ? "block" : "none"}}>
                {this.state.aliasError}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="schedule-description">
              <Form.Label>
                <strong>Description</strong> <span className="text-muted">(optional)</span>
              </Form.Label>
              <Form.Control
                type="text" size="sm" maxLength={DESCRIPTION_MAX_LENGTH}
                value={this.state.description}
                onChange={(e) => this.setState({ description: e.target.value })}
              />
              <Form.Text muted>
                Describes the schedule. Each run's execution carries it too.
              </Form.Text>
            </Form.Group>

            <hr/>

            <Form.Group>
              <Form.Label>
                <strong>Repeat Type</strong>
              </Form.Label>
              <Row>
                <Col sm={12}>
                  <ButtonGroup toggle size="sm" className="scheduler-toggle">
                    {repeatOptions.map((option, idx) => (
                      <ToggleButton
                        key={idx}
                        id={`repeat-${idx}`}
                        type="radio"
                        variant="light"
                        name="repeat"
                        value={option.value}
                        checked={this.state.repeat === option.value}
                        onChange={() => this.setState({ repeat: option.value })}
                      >
                        <span style={{display: "inline-flex", alignItems: "center", gap: "6px"}}>
                          {option.icon} {option.name}
                        </span>
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                </Col>
              </Row>
            </Form.Group>


            {"recurring_cron" === this.state.repeat &&
              <Form.Group>
                <Form.Label>
                  <strong>Cron Expression</strong> <span className="text-muted">(required)</span>
                </Form.Label>
                <Row>
                  <Col sm={12}>
                    <Form.Control
                      type="text" size="sm" required
                      value={this.state.cronExpression}
                      onChange={(e) => this.setState({ cronExpression: e.target.value })}
                      placeholder="0 9 ? * MON-FRI *"
                    />
                  </Col>
                </Row>
                <Form.Text muted>
                  6-field cron format: minute hour day-of-month month day-of-week year — one of day-of-month
                  {" "}and day-of-week must be <strong>?</strong>, not both <strong>*</strong>.
                </Form.Text>
              </Form.Group>
            }
            {"one_time" === this.state.repeat &&
              <Form.Group>
                <Form.Label>
                  <strong>Invocation Time</strong> <span className="text-muted">(required)</span>
                </Form.Label>
                <Row>
                  <Col sm={12}>
                    <Form.Control
                      type="datetime-local" size="sm" required
                      value={this.state.invocationTime}
                      onChange={(e) => this.setState({ invocationTime: e.target.value })}
                    />
                  </Col>
                </Row>
                <Form.Text muted>
                  The time to invoke the schedule, interpreted in the schedule's timezone.
                </Form.Text>
              </Form.Group>
            }


            <Form.Group>
              <Form.Label>
                <strong>Timezone</strong> <span className="text-muted">(required)</span>
              </Form.Label>
              <Row>
                <Col sm={12}>
                  <Select
                    options={TIMEZONE_OPTIONS}
                    value={TIMEZONE_OPTIONS.find(opt => opt.value === this.state.timezone)}
                    onChange={(option) => this.setState({ timezone: option ? option.value : "" })}
                    placeholder="Select timezone..."
                    isClearable={false}
                    menuPortalTarget={document.body}                  />
                </Col>
              </Row>
            </Form.Group>


            {"recurring_cron" === this.state.repeat &&
              <>
                <Row>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>
                        <strong>Start Date</strong>
                      </Form.Label>
                      <Form.Control
                        type="datetime-local" size="sm"
                        value={this.state.startDate}
                        onChange={(e) => this.setState({ startDate: e.target.value })}
                      />
                      <Form.Text muted>
                        Entered and stored in UTC.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>
                        <strong>End Date</strong>
                      </Form.Label>
                      <Form.Control
                        type="datetime-local" size="sm"
                        value={this.state.endDate}
                        onChange={(e) => this.setState({ endDate: e.target.value })}
                      />
                      <Form.Text muted>
                        Entered and stored in UTC.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

              </>
            }

            <hr/>
            <Form.Group>
              <Form.Label>
                <strong>Target Type</strong>
              </Form.Label>
              <Row>
                <Col sm={12}>
                  <ButtonGroup toggle size="sm" className="scheduler-toggle">
                    {targetOptions.map((option, idx) => (
                      <ToggleButton
                        key={idx}
                        id={`target-${idx}`}
                        type="radio"
                        variant="light"
                        name="targetType"
                        value={option.value}
                        checked={this.state.targetType === option.value}
                        onChange={() => this.setState({ targetType: option.value })}
                      >
                        <span style={{display: "inline-flex", alignItems: "center", gap: "6px"}}>
                          {option.icon} {option.name}
                        </span>
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                </Col>
              </Row>
            </Form.Group>


            {"workflow" === this.state.targetType &&
              <>
                <div role="group" aria-labelledby="schedule-target-heading">
                  <Form.Label id="schedule-target-heading">
                    <strong>Where It Runs</strong> <code>target</code>
                  </Form.Label>
                  <Row>
                    <Col sm={6} lg={3}>
                      <Form.Group controlId="schedule-target-org">
                        <Form.Label>
                          <strong>Organization</strong>{" "}
                          <span className="text-muted">{organizationHint(this.state.blob)}</span>
                        </Form.Label>
                        <Form.Control
                          type="text" size="sm"
                          value={this.state.org}
                          isInvalid={this.isInvalid("targetError", "org")}
                          onChange={(e) => this.editTarget({ org: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6} lg={3}>
                      <Form.Group controlId="schedule-target-blob">
                        <Form.Label><strong>Blob</strong></Form.Label>
                        <Form.Control
                          type="text" size="sm"
                          value={this.state.blob}
                          isInvalid={this.isInvalid("targetError", "blob")}
                          onChange={(e) => this.editTarget({ blob: e.target.value })}
                          placeholder="ID or alias"
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6} lg={3}>
                      <Form.Group controlId="schedule-target-revision">
                        <Form.Label><strong>Revision</strong></Form.Label>
                        <Form.Control
                          type="text" size="sm"
                          value={this.state.revision}
                          isInvalid={this.isInvalid("targetError", "revision")}
                          onChange={(e) => this.editTarget({ revision: e.target.value })}
                          placeholder="default or an ID"
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6} lg={3}>
                      <Form.Group controlId="schedule-target-session">
                        <Form.Label><strong>Session</strong></Form.Label>
                        <Form.Control
                          type="text" size="sm"
                          value={this.state.session}
                          isInvalid={this.isInvalid("targetError", "session")}
                          onChange={(e) => this.editTarget({ session: e.target.value })}
                          placeholder="Session ID or alias"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  {this.state.targetError &&
                    <Form.Control.Feedback type="invalid" className="d-block mb-3">
                      {this.state.targetError.message}
                    </Form.Control.Feedback>
                  }
                </div>

                <hr/>
                <div role="group" aria-labelledby="schedule-workflow-heading">
                  <Form.Label id="schedule-workflow-heading">
                    <strong>What It Runs</strong> <code>workflow</code>
                  </Form.Label>
                  <Form.Group>
                    <ButtonGroup toggle size="sm" className="scheduler-toggle"
                      aria-label="Where the workflow comes from">
                      {workflowModeOptions.map((option, idx) => (
                        <ToggleButton
                          key={idx}
                          id={`workflow-mode-${idx}`}
                          type="radio"
                          variant="light"
                          name="workflowMode"
                          value={option.value}
                          checked={this.state.workflowMode === option.value}
                          onChange={() => this.editWorkflow({ workflowMode: option.value })}
                        >
                          {option.name}
                        </ToggleButton>
                      ))}
                    </ButtonGroup>
                  </Form.Group>
                  {WORKFLOW_IN_TARGET === this.state.workflowMode &&
                    <Row>
                      <Col sm={6}>
                        <Form.Group controlId="schedule-workflow-definition">
                          <Form.Label><strong>Workflow</strong></Form.Label>
                          <Form.Control
                            type="text" size="sm" autoComplete="off" list="schedule-workflow-suggestions"
                            value={this.state.workflowDefinition}
                            isInvalid={this.isInvalid("workflowError", "definition")}
                            onFocus={this.loadWorkflowSuggestions}
                            onChange={(e) => this.editWorkflow({ workflowDefinition: e.target.value })}
                            placeholder="Workflow alias or ID"
                          />
                          <datalist id="schedule-workflow-suggestions">
                            {this.state.suggestions.map(definition => (
                              <option key={definition.id} value={definition.alias}>{definition.id}</option>
                            ))}
                          </datalist>
                          <Form.Text muted>{targetWorkflowHelp(this.state.blob, this.state.revision)}</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  }
                  {WORKFLOW_FROM_BLOB === this.state.workflowMode &&
                    <>
                      <Row>
                        <Col sm={6} lg={3}>
                          <Form.Group controlId="schedule-source-org">
                            <Form.Label>
                              <strong>Organization</strong>{" "}
                              <span className="text-muted">{organizationHint(this.state.sourceBlob)}</span>
                            </Form.Label>
                            <Form.Control
                              type="text" size="sm"
                              value={this.state.sourceOrg}
                              isInvalid={this.isInvalid("workflowError", "sourceOrg")}
                              onChange={(e) => this.editWorkflow({ sourceOrg: e.target.value })}
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6} lg={3}>
                          <Form.Group controlId="schedule-source-blob">
                            <Form.Label><strong>Blob</strong></Form.Label>
                            <Form.Control
                              type="text" size="sm"
                              value={this.state.sourceBlob}
                              isInvalid={this.isInvalid("workflowError", "sourceBlob")}
                              onChange={(e) => this.editWorkflow({ sourceBlob: e.target.value })}
                              placeholder="ID or alias"
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6} lg={3}>
                          <Form.Group controlId="schedule-source-revision">
                            <Form.Label><strong>Revision</strong></Form.Label>
                            <Form.Control
                              type="text" size="sm"
                              value={this.state.sourceRevision}
                              isInvalid={this.isInvalid("workflowError", "sourceRevision")}
                              onChange={(e) => this.editWorkflow({ sourceRevision: e.target.value })}
                              placeholder="default or an ID"
                            />
                          </Form.Group>
                        </Col>
                        <Col sm={6} lg={3}>
                          <Form.Group controlId="schedule-source-definition">
                            <Form.Label><strong>Workflow</strong></Form.Label>
                            <Form.Control
                              type="text" size="sm"
                              value={this.state.sourceDefinition}
                              isInvalid={this.isInvalid("workflowError", "sourceDefinition")}
                              onChange={(e) => this.editWorkflow({ sourceDefinition: e.target.value })}
                              placeholder="alias or ID"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Text muted className="mb-3">
                        With revision <code>default</code>, every fire runs what that blob's default revision holds
                        {" "}at the time. A revision ID pins one version.
                      </Form.Text>
                    </>
                  }
                  {this.state.workflowError &&
                    <Form.Control.Feedback type="invalid" className="d-block mb-3">
                      {this.state.workflowError.message}
                    </Form.Control.Feedback>
                  }
                </div>

                <hr/>
                <Form.Group>
                  <Form.Label>
                    <strong>Input Data</strong> <span className="text-muted">(JSON array)</span>
                  </Form.Label>
                  <Row>
                    <Col sm={12}>
                      <Editor
                        name="input-data-editor" width="100%" height="100px" mode="json"
                        wrapEnabled="true"
                        value={this.state.inputData}
                        onChange={(newValue) => {
                          this.setState({inputData: newValue});
                        }}
                      />
                    </Col>
                  </Row>
                  <Form.Text muted>
                    JSON input data for workflow execution.
                  </Form.Text>
                  {inputError &&
                    <Form.Control.Feedback type="invalid" className="d-block">{inputError}</Form.Control.Feedback>
                  }
                </Form.Group>
              </>
            }
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.props.onHide}>Close</Button>
          <Button variant="success" onClick={this.handleSubmit}>{submitLabel}</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
